import d3 from "d3"

export default class Graph2D {
    private containerSelector: string;
    protected svg;
    private xAxis: d3.svg.Axis;
    private yAxis: d3.svg.Axis;
    private x: d3.scale.Linear<number, number>;
    private y: d3.scale.Linear<number, number>;
    private xLabel;
    private yLabel;

    // margin between div - labels and stuff - actual painting area
    private margin = { top: 10, right: 10, bottom: 40, left: 40 };

    private outerWidth; // dimensions of the given dom element
    private outerHeight;
    private width; // actual drawing range for the plot
    private height;

    private xMin;
    private xMax;
    private yMin;
    private yMax;

    private predefXmin;
    private predefXmax;
    private predefYmin;
    private predefYmax;

    private xCat = "x"; // What property of the data holds the x-coordinate (the data object might contain many more properties, i.e: { foo: "MyData", bar: 1234, x: 1.0, y: 2.0 } )
    private yCat = "y";

    private zoomBeh: d3.behavior.Zoom<{}>;
    private line: d3.svg.Line<any>;

    private color = d3.scale.category10();
    private pointRadius = 1.5;
    private lineWidth = 1.0;

    protected objects: any;

    protected get yToMeter(): number {
        return Math.abs(this.y(1.0) - this.y(0.0));
    }

    protected get xToMeter(): number {
        return Math.abs(this.x(1.0) - this.x(0.0));
    }

    public disableResize = false;

    constructor(domElementSelector: string, xMin = -100, xMax = 100, yMin?: number, yMax?: number) {
        this.containerSelector = domElementSelector;

        this.predefXmin = xMin;
        this.predefXmax = xMax;
        this.predefYmin = yMin;
        this.predefYmax = yMax;

        this.x = d3.scale.linear();
        this.y = d3.scale.linear();

        this.resize();
        $(window).on("resize", () => this.resize());
    }

    public resize() {
        if (this.disableResize) {
            return;
        }

        $(this.containerSelector).children().remove();

        this.outerWidth = Math.max(40, $(this.containerSelector).width());
        this.outerHeight = Math.max(300, $(this.containerSelector).height());
        this.width = this.outerWidth - this.margin.left - this.margin.right;
        this.height = this.outerHeight - this.margin.top - this.margin.bottom;

        this.initRanges(this.predefXmin, this.predefXmax, this.predefYmin, this.predefYmax);

        $(this.containerSelector).trigger("resized");
    }

    private initRanges(xMin: number, xMax: number, yMin?: number, yMax?: number): void {
        const ratio = this.height / this.width;

        this.xMin = xMin;
        this.xMax = xMax;
        this.yMin = yMin === undefined ? -(xMax - xMin) * ratio * 0.5 : yMin;
        this.yMax = yMax === undefined ? (xMax - xMin) * ratio * 0.5 : yMax;

        this.x
            .range([0, this.width])
            .domain([this.xMin, this.xMax]);

        this.y
            .range([this.height, 0])
            .domain([this.yMin, this.yMax]);

        const xTicks = this.width / 50;
        const yTicks = this.height / 50;
        this.xAxis = d3.svg.axis()
            .scale(this.x)
            .orient("bottom")
            .ticks(xTicks)
            .tickSize(-this.height); // These create the 'grid'

        this.yAxis = d3.svg.axis()
            .scale(this.y)
            .orient("left")
            .ticks(yTicks)
            .tickSize(-this.width); // These create the 'grid' effect

        this.zoomBeh = d3.behavior.zoom()
            .x(this.x)
            .y(this.y)
            .scaleExtent([1, 1000]) // allowed scale range, no zoom to 1000x (i.e. for the default (x range -100 to 100 , this goes down to reasonable cm level)
            .on("zoom", () => this.zoom());

        this.line = d3.svg.line<any>()
            .x((d) => { return this.x(d[this.xCat]); })
            .y((d) => { return this.y(d[this.yCat]); })
            .interpolate("linear");

        var svg = d3.select(this.containerSelector)
            .append("svg")
            .attr("width", this.outerWidth)
            .attr("height", this.outerHeight)
            .append("g")
            .attr("transform", `translate(${this.margin.left},${this.margin.top})`)
            .call(this.zoomBeh);

        svg.append("rect")
            .attr("width", this.width)
            .attr("height", this.height);

        this.xLabel = svg.append("g")
            .classed("x axis", true)
            .attr("transform", `translate(0,${this.height})`)
            .call(this.xAxis)
            .append("text")
            .classed("label", true)
            .attr("x", this.width)
            .attr("y", this.margin.bottom - 10)
            .style("text-anchor", "end");

        this.yLabel = svg.append("g")
            .classed("y axis", true)
            .call(this.yAxis)
            .append("text")
            .classed("label", true)
            .attr("transform", "rotate(-90)")
            .attr("y", -(this.margin.left - 6))
            .attr("dy", ".71em")
            .style("text-anchor", "end");

        this.objects = svg.append("svg")
            .classed("objects", true)
            .attr("width", this.width)
            .attr("height", this.height);

        this.objects.append("svg:line")
            .classed("axisLine hAxisLine", true)
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", this.width)
            .attr("y2", 0)
            .attr("transform", `translate(0,${this.height})`);

        this.objects.append("svg:line")
            .classed("axisLine vAxisLine", true)
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", 0)
            .attr("y2", this.height);

        var legend = svg.selectAll(".legend")
            .data(this.color.domain())
            .enter().append("g")
            .classed("legend", true)
            .attr("transform", (d, i) => (`translate(0,${i * 20})`));

        legend.append("circle")
            .attr("r", 3.5)
            .attr("cx", this.width + 20)
            .attr("fill", this.color);

        legend.append("text")
            .attr("x", this.width + 26)
            .attr("dy", ".35em")
            .text(d => d);

        this.svg = svg;

        this.markZeroAxes();
    }

    public initScaleAndCenter(initScale: number) {
        this.zoomBeh.scale(initScale)
            .translate([
                -((this.width * initScale) - this.width / 2) / 2,
                -(this.height * initScale) / 2 + 0.9 * this.height
            ]);

        // Update all elements
        this.svg.select(".x.axis").call(this.xAxis);
        this.svg.select(".y.axis").call(this.yAxis);
        this.svg.selectAll("path.line").attr('d', this.line);
        this.svg.selectAll("circle.dot").attr("transform", (d) => this.transform(d));
    }

    private zoom() {
        var e: d3.ZoomEvent = <d3.ZoomEvent>(d3.event),
            // now, constrain the x and y components of the translation by the
            // dimensions of the viewport
            tx = Math.min(0, Math.max(e.translate[0], this.width - this.width * e.scale)),
            ty = Math.min(0, Math.max(e.translate[1], this.height - this.height * e.scale));
        // then, update the zoom behavior's internal translation
        this.zoomBeh.translate([tx, ty]);

        // Update all elements
        this.svg.select(".x.axis").call(this.xAxis);
        this.svg.select(".y.axis").call(this.yAxis);
        this.svg.selectAll("path.line").attr('d', this.line);
        this.svg.selectAll("circle.dot").attr("transform", (d) => this.transform(d));
        this.objects.selectAll("rect")
            .attr("transform", (d) => this.transform(d))
            .attr("width", (d) => this.xToMeter * d.w)
            .attr("height", (d) => this.yToMeter * d.h);

        this.markZeroAxes(); // need to mark again, because xAxis might have removed/readded them

        var self = this;
        this.svg.selectAll(".scaledstrokewidth").style("stroke-width", function () { return this.getAttribute("data-scaledstrokewidth") * (Math.abs(self.y(1.0) - self.y(0.0))) });
    }

    private markZeroAxes() {
        this.svg.selectAll(".axis").selectAll("g").filter(d => Math.abs(Number(d)) <= 0.0000001).classed("zeroTick", true);
    }

    private transform(dataPoint) {
        // this actually selects data from the dataPoint by xCat and then calculates the translate value by passing it to the linear scale x 
        // (same goes for y)
        return `translate(${this.x(dataPoint[this.xCat])},${this.y(dataPoint[this.yCat])})`;
    }

    public removeSeries(seriesName: string) {
        this.objects.selectAll(`.${seriesName}`).remove();
    }

    public renderScatterData(data: ScatterData, renderMode = ScatterDataMode.Points) {
        switch (renderMode) {
            case ScatterDataMode.Polyline:
                var pathData = [];
                pathData.push(data.Data);

                var selectionPath = this.objects
                    .selectAll(`.${data.SeriesId}`)
                    .filter(".line")
                    .data(pathData);

                selectionPath //.attr("transform", transform)
                    .attr("d", this.line); // update

                selectionPath.enter()
                    .append("path")
                    .classed("line", true)
                    .classed(data.SeriesId, true)
                    .attr("d", this.line)
                    .style("stroke-width", this.lineWidth)
                    .style("stroke", data.Color)
                    .style("fill", "none");

                selectionPath.exit()
                    .remove();
                break;

            case ScatterDataMode.Stretch:
                var pairs = [];
                for (var i = 0, len = data.Data.length; i < len; i += 2) {
                    pairs.push([data.Data[i], data.Data[i + 1]]);
                }

                var selectionStretches = this.objects
                    .selectAll(`.${data.SeriesId}`)
                    .filter(".line")
                    .data(pairs);

                //enter
                var tmp = selectionStretches.enter()
                    .insert("path", ":first-child")
                    .classed("line", true)
                    .classed(data.SeriesId, true);

                if (data.ScaleStrokeWidth) {
                    tmp.attr("data-scaledstrokewidth", data.ScaleStrokeWidth)
                        .classed("scaledstrokewidth", true)
                        .style({
                            'stroke': data.Color,
                            'stroke-linecap': "round",
                            'stroke-width': data.ScaleStrokeWidth * this.yToMeter
                        });
                } else {
                    tmp.style({
                        stroke: data.Color,
                        'stroke-width': '2px'
                    });
                }

                //update
                selectionStretches
                    .attr("d", this.line);

                //exit
                selectionStretches.exit().remove();
                break;
            default:
                break;
        }

        var selectionStretches = this.objects
            .selectAll(`.${data.SeriesId}`)
            .filter(".dot")
            .data(data.Data);

        selectionStretches
            .attr("transform", (d) => this.transform(d));// update

        selectionStretches.enter()
            .append("circle")
            .classed("dot", true)
            .classed(data.SeriesId, true)
            .attr("r", this.pointRadius)
            .attr("transform", (d) => this.transform(d))
            .style("fill", data.Color)
            //                .transition() // animation ausgeschaltet
            //                .duration(100)
            //                .attr("y", 0)
            .style("fill-opacity", 1);

        selectionStretches.exit()
            .remove();
    }

    public renderCube(cubeName: string, minPoint: any, maxPoint: any, style: any) {
        const width = Math.abs(maxPoint[this.xCat] - minPoint[this.xCat]);
        const height = Math.abs(maxPoint[this.yCat] - minPoint[this.yCat]);

        const selection = this.objects
            .selectAll(`.${cubeName}`)
            .data([{
                x: minPoint.x,
                y: maxPoint.y, // coordinate system is v-> instead of ^->
                z: minPoint.z,
                w: width,
                h: height
            }]); // dummy ? 

        selection.enter()
            .append("rect")
            .classed(cubeName, true)
            .style(style);

        selection
            .attr("width", (d) => this.xToMeter * d.w)
            .attr("height", (d) => this.yToMeter * d.h)
            .attr("transform", (d) => this.transform(d));

        selection.exit()
            .remove();
    }

    public setXY() { this.xCat = "x"; this.yCat = "y"; }
    public setZX() { this.xCat = "z"; this.yCat = "x"; }
    public setZY() { this.xCat = "z"; this.yCat = "y"; }

    public setLabels(xTxt: string, yTxt: string) {
        this.XLabel = xTxt;
        this.YLabel = yTxt;
    }

    set XLabel(txt: string) {
        this.xLabel.text(txt);
    }

    set YLabel(txt: string) {
        this.yLabel.text(txt);
    }
}

export interface ScatterData {
    SeriesId: string,
    SeriesName: string,
    Data: any[];
    Color: string;
    ScaleStrokeWidth?: number;
}

export enum ScatterDataMode {
    Points,
    Polyline,
    Stretch
}
